import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";
import {
  FormProvider,
  useForm,
  Controller,
  useFieldArray,
} from "react-hook-form";
import TextInput2 from "../../components/form-elements/TexInput2/TextInput2";
import Modal3 from "../../components/modal3/modal3";
import { useMutation } from "react-query";
import {
  getInstitutions,
  getInstitutionsByName,
  removeInvitations,
  sendInvitations,
  storeInstitution,
  updateInstitution,
} from "../../services/institution";
import ReactSelect from "react-select";
import { useTranslation } from "react-i18next";
import { getCountries } from "../../services/location.service";
import useSnackbarStore from "../../store/snackbarStore";
import debounce from "lodash.debounce";
import AsyncSelect from "react-select/async";
import ToggleSwitch2 from "../../components/form-elements/ToggleSwitch2/toggleSwitch2";
import "ag-grid-community/styles/ag-grid.css"; // Core grid CSS, always needed
import "ag-grid-community/styles/ag-theme-alpine.css"; // Optional theme CSS
import Papa from "papaparse";
import DataTable from '../../components/globals/data-table/data-table';

const Institution = () => {
  const [hasParentInstitution, setHasParentInsitution] = useState(false);
  const [hasRegisterInvitations, setHasRegisterInvitations] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [, setDebouncedSearchTerm] = useState("");
  const [isDisabled] = useState(true);
  const [page, setPage] = useState(1);
  const [openModal, setOpenModal] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const methods = useForm();
  const fileInputRef = useRef(null);
  const {
    handleSubmit,
    setValue,
    reset,
    control,
    watch,
    formState: { errors },
  } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "teachers", // Este es el nombre del campo que manejará el array de emails
  });
  const { t, i18n } = useTranslation();
  const { show } = useSnackbarStore();
  const selectedNumLicense = watch("num_licenses");

  const columns = [
    { ref: "id", value: "ID" },
    { ref: "name", value: "Nombre" },
    { ref: "address_line_1", value: "Dirección 1" },
    { ref: "address_line_2", value: "Dirección 2" },
    { ref: "code_dane_institution", value: "Código Dane" },
  ];

  const SelectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: "rgb(182 182 182 / 0.5)",
      borderColor: "transparent",
      borderRadius: "8px",
      caretColor: "white",
      "&:hover": { borderColor: "transparent" }, // Borde en hover
    }),
    input: (provided) => ({
      ...provided,
      color: "rgb(38, 38, 38)",
      fontWeight: "bold", // Establece el color del texto del input a blanco
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? "#F2F2F2" : "#FEFEFE",
      color: "black",
      cursor: "pointer",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "#FEFEFE", // Color del indicador
      "&:hover": {
        color: "#3FB2E3", // Color del indicador en hover
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "rgb(38, 38, 38)", // Establece el color del texto de la opción seleccionada a blanco
      fontWeight: "bold",
    }),
    clearIndicator: (styles) => ({
      ...styles,
      color: "black", // Aquí puedes poner el color que desees
      ":hover": {
        color: "black", // Color cuando se pasa el ratón por encima (hover)
      },
    }),
  };

  const mutationCountries = useMutation(getCountries, {
    onSuccess: (data) => {
      const countryOptions = data.map((country) => ({
        value: country.country_id,
        label: i18n.language === "es" ? country.title_es : country.title_en,
      }));
      setCountries(countryOptions);
    },
    onError: (error) => {},
  });

  const mutationUpdateInstitution = useMutation(updateInstitution, {
    onSuccess: (data) => {
      const parsedInstitution = {
        ...data,
        country: Number(data?.country),
        num_licenses: data?.subscription?.num_licenses || null,
      };
      Object.keys(parsedInstitution).forEach((key) => {
        setValue(key, parsedInstitution[key]);
      });
      setSelectedInstitution(parsedInstitution);
      setLoading(false);

      show("Registro Actualizado");
    },
    onError: (error) => {},
  });

  const mutationStoreInstitution = useMutation(storeInstitution, {
    onSuccess: (data) => {
      const parsedInstitution = {
        ...data,
        country: Number(data?.country),
        num_licenses: data?.subscription?.num_licenses || null,
      };
      Object.keys(parsedInstitution).forEach((key) => {
        setValue(key, parsedInstitution[key]);
      });
      setSelectedInstitution(parsedInstitution);
      setLoading(false);
      show("Registro Creado");
      setSearchLoading(true);
      mutationInstitutions.mutate({ page });
    },
    onError: (error) => {},
  });

  const mutationSendInvitations = useMutation(sendInvitations, {
    onSuccess: () => {
      show("Invitationes Enviadas");
      setLoading(false);
      setSearchLoading(true);
      mutationInstitutions.mutate({ page });
    },
    onError: (error) => {},
  });

  const mutationRemoveInvitations = useMutation(removeInvitations, {
    onSuccess: () => {
      show("Invitationes Eliminadas");
      setLoading(false);
      setSearchLoading(true);
      mutationInstitutions.mutate({ page });
    },
    onError: (error) => {},
  });

  const mutationInstitutions = useMutation(getInstitutions, {
    onSuccess: (res) => {
      const parsedInstitutions = res.results.map((e) => ({
        ...e,
        country: Number(e.country),
        num_licenses: e.subscription?.num_licenses || null,
      }));
      setInstitutions(parsedInstitutions);
      if (selectedInstitution) {
        const findInstitution = res.results.find(
          (e) => e.id === selectedInstitution.id
        );

        if (findInstitution) {
          setSelectedInstitution(findInstitution);
        }
      }
      setSearchLoading(false);
    },
    onError: () => {},
  });

  const handleEditInstitution = (row) => {
    const institution = row.data;
    Object.keys(institution).forEach((key) => {
      setValue(key, institution[key]);
    });
    setValue("logo_color", institution.logo_color);
    setSelectedInstitution(institution);
    setOpenModal(true);
  };

  const handleNewInstitution = () => {
    reset();
    setSelectedInstitution(null);
    setValue("num_licenses", 1);
    setOpenModal(true);
  };

  const onClose = () => {
    setOpenModal(false);
    reset();
    setSelectedInstitution(null);
    setHasParentInsitution(false);
  };

  const onSubmit = (formData) => {
    const formDataToSend = new FormData();
    for (const key in formData) {
      if (formData.hasOwnProperty(key)) {
        if (!["logo_color", "logo_white", "logo_black"].includes(key)) {
          if (key === "teachers") {
            formDataToSend.append(`teachers`, JSON.stringify(formData[key]));
          } else {
            formDataToSend.append(key, formData[key]);
          }
        }
      }
    }
    if (formData.logo_color instanceof File) {
      formDataToSend.append("logo", formData.logo_color);
    }

    if (formData.logo_black instanceof File) {
      formDataToSend.append("logo_black_file", formData.logo_black);
    }

    if (formData.logo_white instanceof File) {
      formDataToSend.append("logo_white_file", formData.logo_white);
    }

    if (formData.teachers && !hasRegisterInvitations) {
      formDataToSend.append("teachers", []);
    }

    if (formData?.parent_institution?.value && hasParentInstitution) {
      formDataToSend.append(
        "parent_institution_id",
        formData.parent_institution.value
      );
    }

    for (let [key, value] of formDataToSend.entries()) {
      console.log(`key ${key} --- value ${value}`);
    }

    setLoading(true);
    const body = formDataToSend;
    if (selectedInstitution) {
      mutationUpdateInstitution.mutate({
        id: selectedInstitution.id,
        body,
      });
    } else {
      mutationStoreInstitution.mutate(body);
    }
  };

  const handlePage = (nextPage) => {
    setPage(nextPage);
    setSearchLoading(true);
    mutationInstitutions.mutate({ page: nextPage });
  };

  const handleCheckboxChange = (email, isChecked) => {
    setSelectedEmails((prevSelectedEmails) => {
      if (isChecked) {
        // Añadir el email al estado si el checkbox está seleccionado
        return [...prevSelectedEmails, email];
      } else {
        // Quitar el email del estado si el checkbox no está seleccionado
        return prevSelectedEmails.filter(
          (selectedEmail) => selectedEmail !== email
        );
      }
    });
  };

  const handleSendInvitations = () => {
    setLoading(true);
    mutationSendInvitations.mutate({ emails: selectedEmails });
  };

  const handleRemoveInvitations = () => {
    setLoading(true);
    mutationRemoveInvitations.mutate({ emails: selectedEmails });
  };

  const onSwitch = (event) => {
    setHasParentInsitution(!!event);
  };

  const onSwitchInvitations = (event) => {
    setHasRegisterInvitations(!!event);
  };

  // eslint-disable-next-line
  const debouncedLoadOptions = useCallback(
    debounce((inputValue, callback) => {
      if (!inputValue) {
        callback([]);
        return;
      }

      // useCallback para memorizar la función debounced
      getInstitutionsByName(inputValue).then((institutions) => {
        const options = institutions.map((institution) => ({
          label: institution.name,
          value: institution.id,
        }));
        if (options.length === 0) {
          // Si no hay opciones, muestra el modal
          setValue("newInstitution", inputValue);
        } else {
          setValue("newInstitution", "");
          // Si hay opciones, asegúrate de que el modal no se muestre
        }
        callback(options);
      });
    }, 1000),
    []
  );

  // eslint-disable-next-line
  const loadInstitutionOptions = (inputValue, callback) => {
    debouncedLoadOptions(inputValue, callback);
  };

  const updateFieldsWithCSVData = (data) => {
    // Filtra los datos para asegurarse de que tienen 'name' y 'email'
    const validData = data.filter((item) => item.name && item.email);
    // Mapea los datos filtrados a la estructura esperada por `useFieldArray`
    const formData = validData.map((item) => ({
      name: item.name,
      email: item.email,
    }));

    // Limpia los campos existentes y establece los nuevos datos
    reset({ ...methods.getValues(), teachers: formData });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
  };

  const handleImport = () => {
    if (!selectedFile) {
      // Puedes mostrar una notificación de que no se ha seleccionado ningún archivo
      return;
    }

    Papa.parse(selectedFile, {
      complete: (result) => {
        // Aquí procesas los resultados
        updateFieldsWithCSVData(result.data);
        setSelectedFile(null);
      },
      header: true,
    });
  };

  const handleFileButtonClick = () => {
    fileInputRef.current.click();
  };

  // eslint-disable-next-line
  const debouncedSearch = useCallback(
    debounce((searchTerm) => {
      setSearchLoading(true);
      mutationInstitutions.mutate({ page, name: searchTerm });
    }, 500),

    [page]
  );

  useEffect(() => {
    // Crea una función debounced que actualiza debouncedSearchTerm
    const debouncedUpdate = debounce((search) => {
      setDebouncedSearchTerm(search);
    }, 500); // 500 ms de espera

    if (searchTerm) {
      debouncedUpdate(searchTerm);
    }

    // Limpia el debounce si el componente se desmonta o el searchTerm cambia
    return () => debouncedUpdate.cancel();
    // eslint-disable-next-line
  }, [searchTerm]);

  useEffect(() => {
    setSearchLoading(true);
    mutationInstitutions.mutate({ page });
    mutationCountries.mutate(i18n.language);
    // eslint-disable-next-line
  }, []);

  useEffect(
    () => {
      const existingLicensesCount =
        selectedInstitution?.subscription?.subscriptionRegistrationCodes
          .length || 0;
      const currentFieldsCount = fields.length;
      const licensesToBeAdded = selectedNumLicense - existingLicensesCount; // Licencias que necesitan ser añadidas

      if (licensesToBeAdded > 0 && licensesToBeAdded > currentFieldsCount) {
        // Añade campos solo si el número de campos es menor que el número de licencias a añadir
        const fieldsToAdd = licensesToBeAdded - currentFieldsCount;
        for (let i = 0; i < fieldsToAdd; i++) {
          append({ name: "", email: "" }); // Añade campos vacíos
        }
      } else if (currentFieldsCount > licensesToBeAdded) {
        for (let i = currentFieldsCount; i > licensesToBeAdded; i--) {
          remove(i - 1); // Remueve campos desde el final
        }
      }
    },
    // eslint-disable-next-line
    [
      selectedNumLicense,
      append,
      fields.length,
      selectedInstitution?.subscription?.subscriptionRegistrationCodes.length,
    ]
  );

  // const columnWidthClasses = "w-1/5";

  return (
    <div>
      <div className="my-4 flex justify-end items-center">
        <div className="relative w-64 mr-4">
          <input
            type="text"
            className="border border-gray-300 rounded-lg py-2 pl-10 pr-4"
            placeholder="Buscar institución"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              debouncedSearch(e.target.value);
            }}
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 text-gray-500 absolute left-3 top-1/2 transform -translate-y-1/2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <button
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded button-primary"
          onClick={() => handleNewInstitution()}
        >
          Agregar
        </button>
      </div>
      <div>
        <DataTable
          data={institutions}
          height={500}
          headerColumns={columns}
          handleSelect={handleEditInstitution}
          loading={searchLoading}
        />
        <div className="flex justify-end">
          <div className=" flex p-2 gap-2">
            {page > 1 && (
              <div>
                <button
                  onClick={() => handlePage(page - 1)}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded button-primary"
                >
                  Anterior
                </button>
              </div>
            )}

            <div>
              <button
                onClick={() => handlePage(page + 1)}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded button-primary"
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      </div>
      <Modal3 isOpen={openModal} onClose={onClose}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="input-text flex flex-row justify-center w-full gap-8">
              <div className="input-label text-shaiaBlue w-full flex items-center justify-center ">
                Institución
              </div>
            </div>
            <div className="input-row">
              {hasParentInstitution && (
                <div className="input-text">
                  <label className="input-label text-shaiaBlue">
                    {t("institution")}
                  </label>
                  <Controller
                    name="parent_institution"
                    control={control}
                    rules={{ required: t("campo_requerido") }}
                    render={({ field }) => (
                      <AsyncSelect
                        {...field}
                        cacheOptions
                        defaultOptions
                        loadOptions={loadInstitutionOptions}
                        onInputChange={(value) => {
                          setSearchTerm(value);
                        }}
                        onChange={(selectedOption) => {
                          field.onChange(selectedOption);
                          setValue("parent_institution", selectedOption, {
                            shouldValidate: true,
                          });
                        }}
                        value={field.value}
                        styles={SelectStyles}
                        placeholder={t(
                          "registro_campo_institution_placeholder"
                        )}
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.value}
                        disabled={!loading && isDisabled}
                      />
                    )}
                  />
                  {errors.institution && (
                    <span className="error">{errors.institution.message}</span>
                  )}
                </div>
              )}

              <div className="input-text flex flex-row justify-end w-full gap-8">
                <div className="input-label text-shaiaBlue w-[70%] flex items-center justify-end ">
                  Tiene institución padre ?
                </div>
                <div className="w-[20%] flex items-center pt-3">
                  <ToggleSwitch2
                    name="lang"
                    onChange={onSwitch}
                    trueLabel="Si"
                    falseLabel="No"
                  />
                </div>
              </div>
            </div>

            <div className="input-row">
              <TextInput2 label="Nombre" name="name" placeHolder="" required />

              <TextInput2
                label="Dirección 1"
                name="address_line_1"
                placeHolder=""
                required
              />

              <TextInput2
                label="Dirección 2"
                name="address_line_2"
                placeHolder=""
              />
            </div>
            <div className="input-row">
              <div className="flex flex-col justify-center mt-7 ">
                <label className="input-label text-shaiaBlue">
                  {t("registro_qPais")}
                </label>
                <Controller
                  name="country"
                  control={control}
                  rules={{ required: t("campo_requerido") }}
                  render={({ field }) => (
                    <ReactSelect
                      {...field}
                      value={countries.find(
                        (option) => option.value === field.value
                      )}
                      options={countries}
                      placeholder={t("seleccionar_pais")}
                      onChange={(option) => {
                        if (option === null) {
                          setValue("country", null);
                        }
                        field.onChange(option?.value);
                      }}
                      styles={SelectStyles}
                      isMulti={false}
                      maxMenuHeight={100}
                      menuPlacement="bottom"
                      isClearable
                    />
                  )}
                />
                {errors.country_code && (
                  <span className="error">{errors.country_code.message}</span>
                )}
              </div>
              <TextInput2
                label="Código Dane"
                name="code_dane_institution"
                placeHolder=""
                required
              />

              <TextInput2
                label="Número de Licencias"
                name="num_licenses"
                placeHolder=""
                minLength={selectedInstitution?.subscription?.num_licenses || 1}
                type="number"
                required
              />
            </div>

            <div className="input-row">
              <div className="flex flex-row">
                <TextInput2
                  label="Logo Color"
                  name="logo_color"
                  placeHolder=""
                  disabled
                />
                <div className="input-text pt-[44px] ">
                  <div className="input-box relative flex align-bottom ">
                    <input
                      type="file"
                      className="absolute inset-0 w-full h-full opacity-0"
                      id="logo"
                      accept="image/*"
                      onChange={(e) => {
                        setValue("logo_color", e.target.files[0]);
                      }}
                    />
                    <label
                      htmlFor="logo"
                      className="bg-blue-500 hover:bg-blue-700 text-white font-bold rounded inline-flex items-center justify-center w-full h-full cursor-pointer"
                    >
                      Buscar
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex flex-row">
                <TextInput2
                  label="Logo Blanco"
                  name="logo_white"
                  placeHolder=""
                  disabled
                />
                <div className="input-text pt-[44px] ">
                  <div className="input-box relative flex align-bottom ">
                    <input
                      type="file"
                      className="absolute inset-0 w-full h-full opacity-0"
                      id="logo"
                      accept="image/*"
                      onChange={(e) => {
                        setValue("logo_white", e.target.files[0]);
                      }}
                    />
                    <label
                      htmlFor="logo"
                      className="bg-blue-500 hover:bg-blue-700 text-white font-bold rounded inline-flex items-center justify-center w-full h-full cursor-pointer"
                    >
                      Buscar
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex flex-row">
                <TextInput2
                  label="Logo Negro"
                  name="logo_black"
                  placeHolder=""
                  disabled
                />
                <div className="input-text pt-[44px] ">
                  <div className="input-box relative flex align-bottom ">
                    <input
                      type="file"
                      className="absolute inset-0 w-full h-full opacity-0"
                      id="logo"
                      accept="image/*"
                      onChange={(e) => {
                        setValue("logo_black", e.target.files[0]);
                      }}
                    />
                    <label
                      htmlFor="logo"
                      className="bg-blue-500 hover:bg-blue-700 text-white font-bold rounded inline-flex items-center justify-center w-full h-full cursor-pointer"
                    >
                      Buscar
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-around mt-6">
              {selectedInstitution && (
                <div className="pt-1">
                  <div className="input-label">Invitaciones Actuales</div>
                  <div className="flex flex-col justify-left text-white pt-4 h-[120px] overflow-y-auto">
                    {selectedInstitution.subscription
                      ? selectedInstitution.subscription.subscriptionRegistrationCodes.map(
                          (e) => (
                            <div className="text-white flex gap-4">
                              <div>
                                {e?.send_email && !e.used ? (
                                  <input
                                    type="checkbox"
                                    className="mr-1"
                                    checked={selectedEmails.includes(
                                      e.send_email
                                    )}
                                    onChange={(event) =>
                                      handleCheckboxChange(
                                        e.send_email,
                                        event.target.checked
                                      )
                                    }
                                  />
                                ) : (
                                  <div className="p-1 pl-3"></div>
                                )}
                              </div>
                              <div>
                                {e?.send_email
                                  ? e?.send_email
                                  : e?.subscription_code}
                              </div>
                              <div
                                style={{
                                  color: e.used ? '#78af64' : '#cf3917',
                                  fontWeight: 'bold'
                                }}
                              >
                                {e.used ? "ACTIVO" : "NO ACTIVO"}
                              </div>
                            </div>
                          )
                        )
                      : "No hay Invitaciones"}
                  </div>
                  {selectedEmails.length ? (
                    <div className="flex justify-around mt-6 gap-2 ">
                      <input
                        type="button"
                        className=" bg-shaiaBlue  text-DarkBlue-500  font-bold rounded text-sm p-2"
                        value="Enviar Invitaciones"
                        onClick={() => handleSendInvitations()}
                      />
                      <input
                        type="button"
                        className=" bg-shaiaBlue  text-DarkBlue-500  font-bold rounded text-sm p-2"
                        value="Eliminar Invitaciones"
                        onClick={() => handleRemoveInvitations()}
                      />
                    </div>
                  ) : null}
                </div>
              )}
              <div>
                <div className="input-text">
                  {hasRegisterInvitations && (
                    <div className="flex flex-col items-center justify-center space-y-4">
                      <div className="input-label text-shaiaBlue w-[80%] flex items-center justify-center">
                        Importar CSV
                      </div>

                      <div className="flex gap-2">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".csv"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                        <button
                          type="button"
                          className="bg-shaiaBlue text-DarkBlue-500 font-bold rounded text-sm p-2"
                          onClick={handleFileButtonClick}
                        >
                          Buscar
                        </button>
                        {selectedFile && (
                          <button
                            type="button"
                            onClick={handleImport}
                            className="bg-shaiaBlue text-DarkBlue-500 font-bold py-2 px-4 rounded"
                          >
                            Importar
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex flex-row  gap-4">
                    <div className="input-label text-shaiaBlue w-[80%] flex items-center justify-end">
                      Registrar Invitaciones
                    </div>
                    <div className="w-[10%] flex items-center  pt-3 ml-4">
                      <ToggleSwitch2
                        name="lang"
                        onChange={onSwitchInvitations}
                        trueLabel="Si"
                        falseLabel="No"
                      />
                    </div>
                  </div>
                </div>
                {hasRegisterInvitations && (
                  <div>
                    <div className="text-center input-label">
                      Nuevas Invitaciones
                    </div>
                    <div className="h-[120px] overflow-y-auto">
                      {selectedNumLicense >
                        (selectedInstitution?.subscription
                          ?.subscriptionRegistrationCodes.length || 0) &&
                        fields.map((field, index) => (
                          <div key={field.id} className="input-row mt-4">
                            <Controller
                              control={control}
                              // rules={{ required: t("campo_requerido") }}
                              name={`teachers[${index}].name`}
                              render={({ field }) => (
                                <input
                                  {...field}
                                  placeholder="Nombre"
                                  className="input-box"
                                />
                              )}
                            />
                            <Controller
                              control={control}
                              name={`teachers[${index}].email`}
                              render={({ field }) => (
                                <input
                                  {...field}
                                  placeholder="Correo"
                                  className="input-box"
                                />
                              )}
                            />
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="input-row mt-4 pt-8">
              <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded button-primary"
              >
                {loading
                  ? "Enviando..."
                  : selectedInstitution
                  ? "Actualizar"
                  : "Crear"}
              </button>
            </div>
          </form>
        </FormProvider>
      </Modal3>
    </div>
  );
};

export default Institution;
